import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { ParticleField } from "../components/site/ParticleField";
import { MagneticButton } from "../components/site/MagneticButton";
import { useCalendly } from "../components/site/CalendlyModal";
import { track } from "../lib/analytics";
import { EASE } from "../components/site/Reveal";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%", filter: "blur(10px)" }}
      animate={{ y: "0%", filter: "blur(0px)" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Chip = ({ label, className, delay }) => (
  <motion.span
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.7, ease: EASE }}
    className={`mono absolute rounded-lg border border-[var(--line)] glass px-3 py-1.5 text-[11px] tracking-[0.2em] text-[var(--cyan)] animate-float ${className}`}
  >
    {label}
  </motion.span>
);

const HeroVisual = () => (
  <div className="relative h-[420px] lg:h-[560px]" aria-hidden="true">
    <motion.svg
      viewBox="0 0 480 480"
      className="absolute inset-0 m-auto h-full w-full max-w-[520px] opacity-70"
      animate={{ rotate: 360 }}
      transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
    >
      <defs>
        <linearGradient id="ring" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00C7A5" />
          <stop offset="50%" stopColor="#00D9FF" />
          <stop offset="100%" stopColor="#1687FF" />
        </linearGradient>
      </defs>
      <circle cx="240" cy="240" r="200" fill="none" stroke="url(#ring)" strokeWidth="1" strokeDasharray="4 14" opacity="0.7" />
      <circle cx="240" cy="240" r="150" fill="none" stroke="url(#ring)" strokeWidth="1" strokeDasharray="60 30" opacity="0.4" />
    </motion.svg>

    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.9, duration: 1.1, ease: EASE }}
      className="absolute left-1/2 top-1/2 w-[270px] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[var(--line)] glass p-5 shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="mono text-[10px] tracking-[0.3em] text-[var(--muted)]">NETSYS // CORE</span>
        <span className="w-2 h-2 rounded-full bg-[var(--green)] animate-pulse-dot" />
      </div>
      {[92, 68, 84, 52, 76].map((wd, i) => (
        <div key={i} className="mb-3 h-1.5 rounded-full bg-[var(--surface2)] overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[var(--green)] to-[var(--blue)]"
            initial={{ width: 0 }}
            animate={{ width: `${wd}%` }}
            transition={{ delay: 1.3 + i * 0.18, duration: 1, ease: EASE }}
          />
        </div>
      ))}
      <div className="mt-5 flex items-center justify-between">
        <span className="mono text-[10px] text-[var(--cyan)]">IA · ACTIVA</span>
        <span className="mono text-[10px] text-[var(--muted)]">v2.0</span>
      </div>
    </motion.div>

    <Chip label="IA" className="left-[8%] top-[14%]" delay={1.4} />
    <Chip label="API" className="right-[6%] top-[24%]" delay={1.55} />
    <Chip label="DATA" className="left-[4%] bottom-[22%]" delay={1.7} />
    <Chip label="AUTOMATION" className="right-[10%] bottom-[12%]" delay={1.85} />
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { openCalendly } = useCalendly();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(-600);
  const my = useMotionValue(-600);
  const gx = useSpring(mx, { stiffness: 60, damping: 20 });
  const gy = useSpring(my, { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set(e.clientX - r.left);
    my.set(e.clientY - r.top);
  };

  const scrollToServices = () => {
    track("hero_cta", { cta: "explorar_soluciones" });
    const el = document.getElementById("servicios");
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -60 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative min-h-screen flex items-center overflow-hidden grain" data-testid="hero">
      <img
        src="https://images.unsplash.com/photo-1644325349124-d1756b79dd42?q=80&w=1920&auto=format&fit=crop"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.12]"
        style={{ maskImage: "radial-gradient(ellipse 75% 65% at 65% 40%, black 30%, transparent 75%)", WebkitMaskImage: "radial-gradient(ellipse 75% 65% at 65% 40%, black 30%, transparent 75%)" }}
        data-testid="hero-bg-image"
      />
      <ParticleField />
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-[var(--green)]/10 blur-[140px]" aria-hidden="true" />
      <div className="absolute -bottom-52 -right-40 w-[620px] h-[620px] rounded-full bg-[var(--blue)]/10 blur-[160px]" aria-hidden="true" />
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute w-[520px] h-[520px] rounded-full"
        style={{
          x: gx,
          y: gy,
          translateX: "-50%",
          translateY: "-50%",
          background: "radial-gradient(circle, rgba(0,199,165,0.13) 0%, rgba(0,217,255,0.06) 40%, transparent 70%)",
        }}
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-32 pb-24 grid items-center gap-14 lg:grid-cols-[1.15fr_1fr]">
        <motion.div style={{ y: yTitle }}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mono mb-7 text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.4em] uppercase text-[var(--green)]"
            data-testid="hero-eyebrow"
          >
            Tecnología · IA · Automatización — Panamá
          </motion.p>
          <h1 className="text-[13.5vw] sm:text-7xl lg:text-[6.2rem] font-extrabold leading-[0.95] tracking-tight" data-testid="hero-title">
            <MaskedLine delay={0.45}>DISEÑAMOS</MaskedLine>
            <MaskedLine delay={0.62}>
              <span className="text-gradient">LO QUE SIGUE.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: EASE }}
            className="mt-7 max-w-xl text-base md:text-lg text-[var(--muted)] leading-relaxed"
            data-testid="hero-subtitle"
          >
            Consultoría tecnológica, aplicaciones inteligentes y automatización con IA para empresas que quieren transformar la manera en que trabajan.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <MagneticButton onClick={scrollToServices} testId="hero-cta-explorar">
              Explorar soluciones <ArrowDown size={15} className="text-[var(--green)]" />
            </MagneticButton>
            <MagneticButton variant="ghost" onClick={openCalendly} testId="hero-cta-agendar">
              Agendar una reunión <ArrowUpRight size={15} className="text-[var(--cyan)]" />
            </MagneticButton>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yVisual }} className="hidden md:block">
          <HeroVisual />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        data-testid="hero-scroll-indicator"
      >
        <span className="mono text-[10px] tracking-[0.4em] text-[var(--muted)]">SCROLL PARA EXPLORAR</span>
        <motion.span animate={{ y: [0, 7, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={15} className="text-[var(--green)]" />
        </motion.span>
      </motion.div>
    </section>
  );
};
